import { type FC, type ReactNode } from 'react';
import IconMdi from '@components/IconMdi';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@components/ui/dropdown-menu';
import { mdiCloseCircle, mdiContentCopy, mdiPencil } from '@mdi/js';
import { useVariationsTabs } from '@providers/variations-tabs';
import { useLanguage } from '@root/src/language';
import { getDataTestId } from '@root/src/utils/element-testid-selectors';

interface SectionMenuProps {
  className?: string;
  isOpen: boolean;
  variationId: string;
  canRemove?: boolean;
  onClose: () => void;
  onRename: () => void;
  children?: ReactNode;
  width: number;
  isEditingName?: boolean;
}

export const TAB_MIN_W = 110; //px

export const VariationMenu: FC<SectionMenuProps> = ({
  className,
  isOpen,
  variationId,
  canRemove,
  onClose,
  onRename,
  children,
  width,
  isEditingName,
}) => {
  const { getContent } = useLanguage({ prefix: 'naming.mrc' });
  const { remove, duplicate } = useVariationsTabs();

  const handleRemoveVariation = () => {
    remove(variationId);
    onClose();
  };

  const handleDuplicateVariation = () => {
    duplicate(variationId);
    onClose();
  };

  const options = [
    {
      testId: getDataTestId('variations.editTabNameButton').toString(),
      path: mdiPencil,
      title: getContent('contextMenus.variation.rename'),
      onClick: onRename,
      isDisabled: false,
    },
    {
      testId: getDataTestId('variations.tabDuplicate').toString(),
      path: mdiContentCopy,
      title: getContent('contextMenus.variation.duplicate'),
      onClick: handleDuplicateVariation,
      isDisabled: false,
    },
    {
      testId: getDataTestId('variations.tabRemoveButton').toString(),
      path: mdiCloseCircle,
      title: getContent('contextMenus.variation.remove'),
      onClick: handleRemoveVariation,
      isDisabled: !canRemove,
    },
  ];

  if (isEditingName) {
    return <>{children}</>;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="center" style={{ minWidth: width }} className={className}>
        {options.map(({ path, onClick, title, isDisabled, testId }) => (
          <DropdownMenuItem
            key={title}
            data-testid={testId}
            disabled={isDisabled}
            onSelect={(event) => {
              event.stopPropagation();
              onClick();
            }}
          >
            <IconMdi path={path} size={0.7} />
            <span>{title}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
